import Vue from 'vue';

export type MatchConfidence = 'EXACT' | 'NAME' | 'DOMAIN_NAME' | 'UNMATCHED';

export interface MeetParticipant {
  actorEmail: string;
  displayName: string;
  maxDurationSeconds: number;
  deviceCount: number;
  deviceTypes: string[];
  organizer: boolean;
  callerType: string | null;
  matchedPersonId: number | null;
  matchedPersonName: string | null;
  matchedPersonRole: 'TEACHER' | 'STUDENT' | null;
  matchConfidence: MatchConfidence;
}

export interface ExpectedParticipant {
  personId: number | null;
  name: string;
  email: string | null;
  role: 'TEACHER' | 'STUDENT';
  foundInMeetLog: boolean;
  matchedActorEmail: string | null;
  matchConfidence: MatchConfidence | null;
}

export interface MeetSessionEntry {
  courseScheduleId: number;
  courseName: string | null;
  sessionDate: string;
  scheduledStartTime: string | null;
  scheduledEndTime: string | null;
  scheduledDurationSeconds: number;
  meetingCode: string;
  teacherName: string | null;
  teacherEmail: string | null;
  teacherId: number | null;
  hasMeetLog: boolean;
  participants: MeetParticipant[];
  expectedParticipants: ExpectedParticipant[];
}

export const fetchIsConfigured = async (): Promise<boolean> => {
  const response = await Vue.axios.get('/meet-report/is-configured');
  return response.data?.data?.enabled === true;
};

export interface TutorPerformanceEntry {
  teacherId: number;
  teacherName: string;
  teacherEmail: string;
  totalScheduledSessions: number;
  sessionsWithMeetLog: number;
  sessionsMissingMeetLog: number;
  avgDurationRatio: number;
  avgActualDurationSeconds: number;
}

export const fetchSessionAudit = async (
  startDate: string,
  endDate: string,
  teacherId?: number | null,
): Promise<MeetSessionEntry[]> => {
  const params: Record<string, unknown> = { startDate, endDate };
  if (teacherId) params.teacherId = teacherId;

  const response = await Vue.axios.get('/meet-report/session-audit', {
    params,
  });
  return response.data?.data ?? [];
};

export const fetchTutorPerformance = async (
  startDate: string,
  endDate: string,
): Promise<TutorPerformanceEntry[]> => {
  const response = await Vue.axios.get('/meet-report/tutor-performance', {
    params: { startDate, endDate },
  });
  return response.data?.data ?? [];
};

export const formatDuration = (seconds: number | null | undefined): string => {
  if (!seconds) return '—';
  const totalMins = Math.floor(seconds / 60);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

export const formatDurationRatio = (ratio: number): string => {
  if (!ratio && ratio !== 0) return '—';
  return `${Math.round(ratio * 100)}%`;
};
